import React, { useContext, useEffect, useMemo, useState } from 'react';

import {
    ContractDeal,
    GetContractDealsByIdsInput,
    GetContractDealsByIdsOutput
} from '@amzn/coral_com-amazon-musicpapercontractconfigurationservice';
import { UnknownCoralError } from '@amzn/ihm-js-coral-client';
import minusCircleTokens from '@amzn/meridian-tokens/base/icon/minus-circle';
import Alert from '@amzn/meridian/alert';
import Button from '@amzn/meridian/button';
import Column from '@amzn/meridian/column';
import Heading from '@amzn/meridian/heading';
import Icon from '@amzn/meridian/icon';
import Row from '@amzn/meridian/row';
import Text from '@amzn/meridian/text';

import { ColumnState, GridApi, RowNode, SelectionChangedEvent } from 'ag-grid-community';
import { cloneDeep, isEmpty, toArray } from 'lodash';
import { contractConfigurationClient } from 'src/api/client';
import { BaseAgGrid } from 'src/components/BaseAgGrid';
import { Status } from 'src/constants';
import { BREADCRUMB_HEIGHT, FOOTER_HEIGHT, HEIGHT_ADJUSTMENT, TOPBAR_HEIGHT } from 'src/constants/style';
import { AddDealContext } from 'src/contexts/addDealContext';
import { AppContext } from 'src/contexts/appContext';
import Arrow from 'src/images/arrow.svg';
import BlueDot from 'src/images/bluedot.svg';
import GreenDot from 'src/images/greendot.svg';
import { ISelectVendorAgGridRowData, ISelectVendorVendorToStatusObj, IVendorSelected } from 'src/types/IAddDeal';
import { getWindowSize } from 'src/util/PlanMappings';
import { getStatusCellRenderer } from 'src/util/grid/cellRenderer';
import { getGeneralStatus } from 'src/util/grid/status';

import { DataSelector } from '../../CompareContracts/DataSelector/data-selector';
import './SelectVendor.scss';

export const SelectVendor = () => {
    const { services, deals, vendors, selectedVendors, setSelectedVendors, setSpecifyDealGridRowData } =
        React.useContext(AddDealContext);

    const [isDataLoaded, setIsDataLoaded] = useState(false);
    const [selectVendorGridRowData, setSelectVendorGridRowData] = React.useState<ISelectVendorAgGridRowData[]>([]);
    const [contracts, setContracts] = useState<ContractDeal[]>([]);
    const [selectedContractId, setSelectedContractId] = useState<string | undefined>(undefined);
    const [contractVendorMap, setContractVendorMap] = useState<
        Record<string, Array<{ entityId: number; entityName: string }>>
    >({});
    const [agGridApi, setAgGridApi] = useState<GridApi>();

    const { setTopAlertMsg } = useContext(AppContext);

    // Create contract options for DataSelector
    const contractIdMap = useMemo(
        () =>
            contracts.reduce((acc, curr) => {
                acc.set(curr.contractId.toString(), curr.contractName);
                return acc;
            }, new Map<string, string>()),
        [contracts]
    );

    useEffect(() => {
        // reset grid data in specify deal screen
        setSpecifyDealGridRowData([]);

        contractConfigurationClient
            .getContractDealsByIds(
                GetContractDealsByIdsInput.clone({
                    includeDeletedContracts: false,
                    includeDraftContracts: false,
                    includeDeletedContractDeals: false,
                    includeIneligibleContractDeals: true
                })
            )
            .subscribe(
                (response: GetContractDealsByIdsOutput) => {
                    const vendorMap: Record<string, Array<{ entityId: number; entityName: string }>> = {};
                    response.contractDeals.forEach((contract) => {
                        const eligibleEntities = contract.entityActiveIntervals
                            .filter((entityActiveInterval) => getGeneralStatus(entityActiveInterval) === Status.Active)
                            .map((entity) => ({
                                entityId: entity.entityId,
                                entityName: entity.entityName
                            }));

                        vendorMap[contract.contractId.toString()] = eligibleEntities;
                    });

                    setContractVendorMap(vendorMap);
                    setContracts(
                        response.contractDeals.filter(
                            (contractDeal) => !isEmpty(vendorMap[contractDeal.contractId.toString()])
                        )
                    );
                },
                (error: UnknownCoralError) => {
                    // eslint-disable-next-line no-console
                    console.error('GetContractDealsByIds Unexpected error', error.receivedException.message);
                    setTopAlertMsg(error.receivedException.message);
                }
            );
    }, []);

    // use the deals and vendors data from Context to populate whether certain vendor has active and upcoming deals per service
    // and contruct row data for the AG Grid table
    useEffect(() => {
        if (!isEmpty(deals) || !isEmpty(vendors)) {
            const vendorToDealStatusObj = deals?.reduce((acc, curr) => {
                if (!acc[curr.entityId!]) {
                    acc[curr.entityId!] = { entityName: curr.entityName! };
                }
                if (curr.status === Status.Upcoming || curr.status === Status.Active) {
                    if (!acc[curr.entityId!][curr.serviceName!]) {
                        acc[curr.entityId!][curr.serviceName!] = new Set();
                    }
                    // @ts-ignore
                    if (acc[curr.entityId!][curr.serviceName!].size < 2) {
                        // @ts-ignore
                        acc[curr.entityId!][curr.serviceName!].add(curr.status);
                    }
                }
                return acc;
            }, {} as ISelectVendorVendorToStatusObj);

            const gridRowData = [] as ISelectVendorAgGridRowData[];

            vendors
                // filter out vendors that have no vendor codes at launched territories
                .filter((vendor) => (vendor.entityVendorCodes?.length || 0) > 0)
                .forEach((entity) => {
                    const newRow = {} as ISelectVendorAgGridRowData;
                    newRow['entityName'] = entity.entityName!;
                    newRow['entityId'] = entity.entityId!;

                    if (vendorToDealStatusObj[entity.entityId!]) {
                        const vendorToDealStatusObjValue = cloneDeep(vendorToDealStatusObj[entity.entityId!]);
                        for (const vendorObjKey in vendorToDealStatusObjValue) {
                            if (vendorObjKey !== 'entityName') {
                                const statusArr = toArray(vendorToDealStatusObjValue[vendorObjKey]);
                                statusArr.sort();
                                newRow[vendorObjKey] = statusArr.join(', ');
                            }
                        }
                    }
                    gridRowData.push(newRow);
                });

            setSelectVendorGridRowData(gridRowData);
            setIsDataLoaded(true);
        }
    }, [deals, vendors]);

    const colDefs = [
        {
            headerName: 'Vendor',
            field: 'entityName',
            filter: 'agTextColumnFilter',
            headerCheckboxSelection: true,
            headerCheckboxSelectionFilteredOnly: true,
            checkboxSelection: true,
            comparator: (valueA: string, valueB: string) => {
                return valueA.toLowerCase().localeCompare(valueB.toLowerCase());
            },
            flex: 3
        },
        ...services
            .sort((service1, service2) => service1.serviceName!.localeCompare(service2.serviceName!))
            .map((service) => ({
                headerName: service.serviceName,
                field: service.serviceName,
                cellRenderer: getStatusCellRenderer,
                flex: 1,
                wrapHeaderText: true
            }))
    ];

    const sortModel: ColumnState[] = [
        { colId: 'entityName', sort: 'asc', sortIndex: 0 },
        { colId: 'Unlimited', sort: 'asc', sortIndex: 1 }
    ];

    const checkboxSelectionChangeHandler = (event: SelectionChangedEvent) => {
        const { api } = event;
        const newSelectVendors: IVendorSelected[] = api.getSelectedNodes().map((node) => {
            return {
                name: node.data.entityName,
                entityId: node.data.entityId,
                rowNode: node as RowNode
            };
        });
        setSelectedVendors(newSelectVendors);
    };

    const removeSelectedVendor = (rowNode: RowNode) => {
        rowNode.setSelected(false);
    };

    // Handle contract filter selection
    const handleContractChange = (contractId: string | undefined) => {
        setSelectedContractId(contractId);

        if (!contractId || !agGridApi) {
            return;
        }

        const vendorList = contractVendorMap[contractId];

        if (vendorList && vendorList.length > 0) {
            // Clear previous selections
            agGridApi.deselectAll();

            // Get entity IDs from the contract's vendor list
            const entityIds = vendorList.map((entity) => entity.entityId.toString());

            // Select rows based on entity IDs
            agGridApi.forEachNode((node) => {
                if (entityIds.includes(node.data.entityId?.toString())) {
                    (node as RowNode).setSelected(true);
                }
            });
        }
    };

    const handleClearBtnClick = () => {
        setSelectedContractId(undefined);
        if (agGridApi) {
            agGridApi.deselectAll();
        }
    };

    const getSelectedVendorCount = () => {
        if (!selectedContractId) return 0;
        const vendorList = contractVendorMap[selectedContractId];
        return vendorList ? vendorList.length : 0;
    };

    return (
        <div className="select-vendor-div">
            <Heading className="select-vendor-header" level={4}>
                Select vendors
            </Heading>

            <Column spacingInset="0 0 400 0">
                <Row spacing="300">
                    <DataSelector
                        id={selectedContractId}
                        setId={handleContractChange}
                        idMap={contractIdMap}
                        title="contract"
                        prefix="Filter by active contract"
                    />

                    {selectedContractId && (
                        <Button type="secondary" onClick={handleClearBtnClick}>
                            Clear
                        </Button>
                    )}
                    {selectedContractId && (
                        <Alert size="medium" type="neutral">
                            {getSelectedVendorCount()} vendors selected from contract filter
                        </Alert>
                    )}
                </Row>
            </Column>

            <div
                className="flex-div"
                style={{
                    height:
                        getWindowSize().height -
                        (TOPBAR_HEIGHT + BREADCRUMB_HEIGHT + FOOTER_HEIGHT + HEIGHT_ADJUSTMENT * 5)
                }}
            >
                <div className="left-grid">
                    <BaseAgGrid
                        columnDefs={colDefs}
                        sortModel={sortModel}
                        rowData={selectVendorGridRowData}
                        onSelectionChanged={checkboxSelectionChangeHandler}
                        persistedSelectedRowNodes={selectedVendors.map((vendor) => vendor.rowNode)}
                        rowMultiSelectWithClick={true}
                        suppressSizeColumnsToFit={true}
                        setAgGridApi={setAgGridApi}
                        loading={!isDataLoaded}
                    />
                </div>
                <div className="mid-arrow">
                    <img src={Arrow} alt="blue right arrow" />
                </div>
                <div className="right-list">
                    {isEmpty(selectedVendors) ? (
                        <div className="empty-list">Select one or more vendors.</div>
                    ) : (
                        <div className="selected-vendor-list">
                            <Row>
                                <Heading level={5}>Vendor name</Heading>
                            </Row>
                            {selectedVendors
                                .sort((vendorA, vendorB) =>
                                    vendorA.name.toLowerCase().localeCompare(vendorB.name.toLowerCase())
                                )
                                .map((vendor) => (
                                    <Row key={vendor.name} alignmentHorizontal="justify">
                                        <Column>
                                            <Text>{vendor.name}</Text>
                                        </Column>
                                        <Column>
                                            <Button
                                                type="link"
                                                size="small"
                                                onClick={() => removeSelectedVendor(vendor.rowNode)}
                                            >
                                                <Icon tokens={minusCircleTokens} />
                                            </Button>
                                        </Column>
                                    </Row>
                                ))}
                        </div>
                    )}
                </div>
            </div>
            <Row alignmentHorizontal="start" spacingInset="300">
                <Row spacing="200">
                    <img src={GreenDot} alt="active service" />
                    <Text type="b200">Active</Text>
                </Row>
                <Row spacing="200">
                    <img src={BlueDot} alt="upcoming service" />
                    <Text type="b200">Upcoming</Text>
                </Row>
                <Row>
                    <Text type="b200">— No active or upcoming deals</Text>
                </Row>
            </Row>
        </div>
    );
};
